import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shareDailyReport, todayISO } from "@/lib/pgt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, ClipboardCheck, Phone, Package, Users, FileText, Send, Share2 } from "lucide-react";
import { toast } from "sonner";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function Stat({ label, done, target }) {
  const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-2.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-stone-500">{label}</span>
        <span className="font-semibold text-stone-800">{done}{target ? ` / ${target}` : ""}</span>
      </div>
      {target > 0 && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-100">
          <div className="h-full rounded-full bg-forest" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export default function DailyReportDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [form, setForm] = useState({ calls_made: "", samples_dispatched: "", meetings: "", quotations_shared: "", summary: "", blockers: "", plan_tomorrow: "" });
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["daily-report-me"],
    queryFn: async () => (await api.get("/daily-reports/me")).data,
    enabled: open,
  });

  useEffect(() => {
    if (data?.report) {
      const r = data.report;
      setForm({
        calls_made: r.calls_made ?? "", samples_dispatched: r.samples_dispatched ?? "",
        meetings: r.meetings ?? "", quotations_shared: r.quotations_shared ?? "",
        summary: r.summary ?? "", blockers: r.blockers ?? "", plan_tomorrow: r.plan_tomorrow ?? "",
      });
    }
  }, [data?.report]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const snap = data?.snapshot;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/daily-reports", {
        calls_made: Number(form.calls_made) || 0,
        samples_dispatched: Number(form.samples_dispatched) || 0,
        meetings: Number(form.meetings) || 0,
        quotations_shared: Number(form.quotations_shared) || 0,
        summary: form.summary || null, blockers: form.blockers || null, plan_tomorrow: form.plan_tomorrow || null,
      });
      toast.success("Daily report submitted");
      qc.invalidateQueries({ queryKey: ["daily-report-me"] });
      qc.invalidateQueries({ queryKey: ["daily-report-status"] });
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const share = async () => {
    setSharing(true);
    try {
      const r = await shareDailyReport(todayISO(), user?.name);
      if (r === "cancelled") return;
      if (r === "downloaded") toast("Report PDF saved — attach it in WhatsApp");
      else if (r === "shared") toast.success("Ready to share report");
    } catch (err) { toast.error("Could not prepare the report PDF"); }
    finally { setSharing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="daily-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-forest" /> Daily work report</DialogTitle>
          <DialogDescription>{data?.submitted ? "You already submitted today — you can update it." : "Review today's numbers and add your own activity, then submit."}</DialogDescription>
        </DialogHeader>

        {isLoading || !snap ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Daily task vs today</div>
              <Stat label="Messages sent" done={snap.messages.done} target={snap.messages.target} />
              {snap.messages.note && <p className="mt-1 text-[11px] text-stone-500">Task note: {snap.messages.note}</p>}
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Weekly goal vs daily achievement</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-stone-200 bg-white p-2.5">
                  <div className="text-[11px] text-stone-500">Conversions today</div>
                  <div className="font-heading text-lg font-bold text-forest">{snap.today.conversions}</div>
                  <div className="text-[10px] text-stone-400">Week: {snap.week.conversions_done} / {snap.week.conversions_target || "—"}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-2.5">
                  <div className="text-[11px] text-stone-500">Revenue today</div>
                  <div className="font-heading text-lg font-bold text-gold">{rupee(snap.today.revenue)}</div>
                  <div className="text-[10px] text-stone-400">Week: {rupee(snap.week.revenue_done)} / {snap.week.revenue_target ? rupee(snap.week.revenue_target) : "—"}</div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Auto-tracked today</div>
              <div className="grid grid-cols-5 gap-1.5 text-center">
                {[["Responses", snap.activity.responses], ["Follow-ups", snap.activity.followups_completed], ["Stage", snap.activity.stage_changes], ["Notes", snap.activity.notes], ["New leads", snap.activity.new_customers]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-stone-50 p-1.5">
                    <div className="font-heading text-base font-bold text-stone-800">{v}</div>
                    <div className="text-[9px] text-stone-400">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Your activity (self-reported)</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-xs"><Phone className="h-3.5 w-3.5" /> Calls made</Label>
                  <Input type="number" min="0" value={form.calls_made} onChange={(e) => set("calls_made", e.target.value)} placeholder="0" data-testid="report-calls" />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-xs"><Package className="h-3.5 w-3.5" /> Samples dispatched</Label>
                  <Input type="number" min="0" value={form.samples_dispatched} onChange={(e) => set("samples_dispatched", e.target.value)} placeholder="0" data-testid="report-samples" />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-xs"><Users className="h-3.5 w-3.5" /> Meetings / visits</Label>
                  <Input type="number" min="0" value={form.meetings} onChange={(e) => set("meetings", e.target.value)} placeholder="0" data-testid="report-meetings" />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1 text-xs"><FileText className="h-3.5 w-3.5" /> Quotations / rates shared</Label>
                  <Input type="number" min="0" value={form.quotations_shared} onChange={(e) => set("quotations_shared", e.target.value)} placeholder="0" data-testid="report-quotations" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs">What did you accomplish today?</Label>
                <Textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="Key wins, conversations, orders…" data-testid="report-summary" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Blockers / help needed</Label>
                <Textarea rows={2} value={form.blockers} onChange={(e) => set("blockers", e.target.value)} placeholder="Pricing approval, stock, samples pending…" data-testid="report-blockers" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Plan for tomorrow</Label>
                <Textarea rows={2} value={form.plan_tomorrow} onChange={(e) => set("plan_tomorrow", e.target.value)} placeholder="Priority follow-ups and targets…" data-testid="report-plan" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {data?.submitted && (
              <Button variant="outline" onClick={share} disabled={sharing || isLoading} className="border-whatsapp/40 text-whatsapp hover:bg-whatsapp/10" data-testid="dialog-share-daily-report">
                {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Share2 className="mr-1.5 h-4 w-4" /> Share on WhatsApp</>}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={submit} disabled={saving || isLoading} data-testid="report-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> {data?.submitted ? "Update report" : "Submit report"}</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
